import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import TrashIcon from '@heroicons/react/24/outline/TrashIcon';
import { useState } from 'react';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { useTranslation } from '@/hooks/useTranslation';
import type { AssetLabel } from '@/pages/dam/api';
import { cn } from '@/services/utils';

import { DamPanelOption } from '../DamPanelOption';
import { DamTagNameField } from '../DamTagNameField';

interface DamTagRowProps {
    label: AssetLabel;
    isSelected: boolean;
    onToggle: () => void;
    /** Saves the new name; resolves `false` when it failed and the row stays in rename mode. */
    onRename: (name: string) => Promise<boolean>;
    onDelete: () => void;
    /** Validation message for a new name, e.g. "This tag already exists."; `undefined` when valid. */
    validateName: (name: string) => string | undefined;
}

/**
 * Tag row of the tags panel (wireframe `I6880:168354;6476:92549;7169:21032`): the tag as
 * filter option, and edit and delete on hover or keyboard focus (star dropped, D-019).
 * Edit renames the tag in place: Enter saves, Escape cancels; there is no merge (D-038).
 */
function DamTagRow({ label, isSelected, onToggle, onRename, onDelete, validateName }: DamTagRowProps) {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<string | null>(null);
    const [error, setError] = useState<string>();
    const [isSaving, setIsSaving] = useState(false);

    const stopEditing = () => {
        setDraft(null);
        setError(undefined);
    };

    const save = async () => {
        if (draft === null || isSaving) {
            return;
        }
        const name = draft.trim();
        if (!name || name === label.name) {
            stopEditing();
            return;
        }
        const validation = validateName(name);
        if (validation) {
            setError(validation);
            return;
        }
        setIsSaving(true);
        const renamed = await onRename(name);
        setIsSaving(false);
        if (renamed) {
            stopEditing();
        }
    };

    if (draft !== null) {
        return (
            <div className="tw-py-1">
                <DamTagNameField
                    value={draft}
                    onChange={(value) => {
                        setDraft(value);
                        setError(undefined);
                    }}
                    onSubmit={() => void save()}
                    onCancel={stopEditing}
                    error={error}
                    disabled={isSaving}
                    label={t('DAM.TAGS.RENAME', { name: label.name })}
                    autoFocus
                />
            </div>
        );
    }

    return (
        <div className="tw-group tw-flex tw-items-center tw-gap-1 tw-rounded hover:tw-bg-fill-primary-hover">
            <div className="tw-min-w-0 tw-flex-1">
                <DamPanelOption label={label.name} count={label.assetCount} isSelected={isSelected} onToggle={onToggle} />
            </div>
            <div
                className={cn(
                    'tw-flex tw-shrink-0 tw-opacity-0 tw-transition-opacity tw-duration-100 tw-ease-in-out',
                    'group-hover:tw-opacity-100 group-focus-within:tw-opacity-100',
                )}
            >
                <ButtonPanel
                    isDisabled={false}
                    icon={PencilIcon}
                    aria-label={t('DAM.TAGS.RENAME', { name: label.name })}
                    tooltip={t('DAM.TAGS.RENAME', { name: label.name })}
                    onClick={() => setDraft(label.name)}
                />
                <ButtonPanel
                    isDisabled={false}
                    icon={TrashIcon}
                    variant="destructive"
                    aria-label={t('DAM.TAGS.DELETE', { name: label.name })}
                    tooltip={t('DAM.TAGS.DELETE', { name: label.name })}
                    onClick={onDelete}
                />
            </div>
        </div>
    );
}

export { DamTagRow };
export type { DamTagRowProps };
